import { createContext, useContext, type ReactNode } from 'react';
import { useLoad } from '@affdice/portal-kit';

import type { PlatformApi } from '../client.ts';
import { NO_ABILITIES, abilitiesFrom, type Abilities } from './entity-actions.ts';
import { myCapabilities } from './login-as/LoginAs.tsx';

/**
 * What the signed-in platform user may do, read once per page load through the
 * same cached read the Log in as button uses (`mine` on
 * GET /v1/platform/permissions), and handed to every screen below the shell
 * so a popup three levels down need not be passed it.
 *
 * While it loads, and if it cannot be read, the answer is "nothing": a button
 * that appears a moment late is better than one that appears and is refused.
 * `ready` lets a screen tell "still reading" from "holds nothing".
 */
interface AbilitiesValue {
  readonly ready: boolean;
  readonly can: Abilities;
}

const AbilitiesContext = createContext<AbilitiesValue>({ ready: false, can: NO_ABILITIES });

export function AbilitiesProvider({
  api,
  superAdmin,
  children,
}: {
  api: PlatformApi;
  /** Only the super admin role itself — not a capability anyone can be granted. */
  superAdmin: boolean;
  children: ReactNode;
}) {
  const caps = useLoad(() => myCapabilities(api), []);
  const value: AbilitiesValue = caps.state === 'ready'
    ? { ready: true, can: abilitiesFrom(caps.value, superAdmin) }
    : { ready: false, can: NO_ABILITIES };
  return <AbilitiesContext.Provider value={value}>{children}</AbilitiesContext.Provider>;
}

export function useAbilities(): AbilitiesValue {
  return useContext(AbilitiesContext);
}
